import React, { useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { switchLanguage } from "@envirohub/i18n";
import { auth, getErrorCodeMessage } from "@envirohub/utils";
import {
  logoutServerSession,
  useCustomerContext,
  useNotifications,
  useMarkNotificationRead,
  useSwitchContext,
  type AuthContext,
} from "@/api";
import { ContextSwitcherMenuItems } from "@/components";
import { rememberLastContext } from "@/lib/contexts";
import { resolveNotificationPath } from "@/lib/notificationDeepLink";
import {
  KsaFlag,
  UkFlag,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  NotificationBell,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  toast,
} from "@envirohub/ui";
import {
  Home,
  Activity,
  HelpCircle,
  User,
  LogOut,
  Layers,
  ClipboardList,
  MapPin,
  Wallet,
  Shield,
  Users,
} from "lucide-react";

function BrandMark() {
  return (
    <NavLink to="/" className="flex items-center gap-2.5">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl gradient-primary text-white shadow-float">
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
          />
        </svg>
      </div>
      <div className="flex flex-col leading-none">
        <span className="font-headline text-lg font-extrabold text-surface-inverse-on">
          EnviroHub
        </span>
        <span className="mt-0.5 text-micro font-bold uppercase tracking-widest text-outline">
          Services
        </span>
      </div>
    </NavLink>
  );
}

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === "en";

  return (
    <Select
      value={isEnglish ? "en" : "ar"}
      onValueChange={(val) => {
        if (val !== i18n.language) switchLanguage(val as "en" | "ar");
      }}
    >
      <SelectTrigger
        className="h-10 w-[92px] shrink-0 border-none bg-white/5 px-3 text-surface-inverse-on/70 hover:bg-white/10 hover:text-surface-inverse-on focus:outline-none focus:ring-2 focus:ring-primary"
        title="Select language"
      >
        <div className="flex items-center gap-2">
          {isEnglish ? (
            <UkFlag size={18} style={{ borderRadius: "50%", overflow: "hidden" }} />
          ) : (
            <KsaFlag size={18} style={{ borderRadius: "50%", overflow: "hidden" }} />
          )}
          <span className="font-body text-xs font-semibold uppercase">
            {isEnglish ? "EN" : "AR"}
          </span>
        </div>
      </SelectTrigger>
      <SelectContent className="min-w-[120px] rounded-xl border border-white/10 bg-surface-inverse p-1 shadow-modal">
        <SelectItem
          value="en"
          className="text-surface-inverse-on/80 focus:bg-white/10 focus:text-surface-inverse-on"
        >
          <div className="flex items-center gap-2">
            <UkFlag size={18} style={{ borderRadius: "50%", overflow: "hidden" }} />
            <span className="font-body text-xs font-semibold">EN</span>
          </div>
        </SelectItem>
        <SelectItem
          value="ar"
          className="text-surface-inverse-on/80 focus:bg-white/10 focus:text-surface-inverse-on"
        >
          <div className="flex items-center gap-2">
            <KsaFlag size={18} style={{ borderRadius: "50%", overflow: "hidden" }} />
            <span className="font-body text-xs font-semibold">عربي</span>
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

export default function PortalLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isB2B } = useCustomerContext();
  const { data: notificationsData, isLoading: notificationsLoading } = useNotifications({
    limit: 20,
  });
  const { mutate: markRead } = useMarkNotificationRead();
  const switchContext = useSwitchContext();

  const notifications = notificationsData?.items ?? [];

  const handleLogout = () => {
    logoutServerSession().catch(() => {});
    auth.clearAuth();
    navigate("/login");
  };

  // The hook stores the new tokens and clears every cached query; the current
  // page may belong to the previous context, so start again from home.
  const handleSwitchContext = (context: AuthContext) => {
    switchContext.mutate(
      { scope: context.scope, membershipId: context.membershipId },
      {
        onSuccess: (switched) => {
          rememberLastContext(switched.activeContext);
          navigate("/", { replace: true });
          toast.success(
            t("nav.switchedTo", {
              name: switched.activeContext.organizationName ?? switched.activeContext.label,
            }),
          );
        },
        onError: (error) => toast.error(getErrorCodeMessage(error, t)),
      },
    );
  };

  const handleNotificationClick = useCallback(
    (item: { entityType: string; entityId: string }) => {
      const path = resolveNotificationPath(item.entityType, item.entityId);
      if (path) navigate(path);
    },
    [navigate],
  );

  const navItems = [
    { to: "/", labelKey: "nav.home", icon: Home },
    { to: "/services", labelKey: "nav.services", icon: Layers },
    { to: "/requests", labelKey: "nav.requests", icon: ClipboardList },
    { to: "/orders", labelKey: "nav.orders", icon: Activity },
    { to: "/contact", labelKey: "nav.contact", icon: HelpCircle },
  ];

  const profileLinks = [
    { to: "/profile/account", labelKey: "profile.tabs.account", icon: User, b2bOnly: false },
    { to: "/profile/locations", labelKey: "profile.tabs.locations", icon: MapPin, b2bOnly: false },
    { to: "/profile/wallet", labelKey: "profile.tabs.wallet", icon: Wallet, b2bOnly: false },
    { to: "/profile/team", labelKey: "profile.tabs.team", icon: Users, b2bOnly: true },
    { to: "/profile/security", labelKey: "profile.tabs.security", icon: Shield, b2bOnly: false },
  ].filter((link) => !link.b2bOnly || isB2B);

  return (
    <div className="flex min-h-screen flex-col bg-surface pb-24 text-surface-on md:pb-0">
      <header className="fixed top-0 z-50 w-full bg-surface-inverse text-surface-inverse-on">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6">
          <div className="flex items-center gap-8">
            <BrandMark />

            <nav className="hidden items-center gap-1 md:flex" aria-label={t("nav.home")}>
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-2 text-micro font-bold uppercase tracking-widest transition-colors duration-150 ${
                      isActive
                        ? "bg-primary/15 text-primary"
                        : "text-surface-inverse-on/55 hover:bg-white/5 hover:text-surface-inverse-on"
                    }`
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <div className="hidden items-center gap-3 md:flex">
              <NotificationBell
                notifications={notifications}
                isLoading={notificationsLoading}
                onMarkRead={markRead}
                onNotificationClick={handleNotificationClick}
                label={t("nav.notifications")}
                triggerClassName="border-none bg-white/5 text-surface-inverse-on/70 hover:bg-white/10 hover:text-surface-inverse-on"
              />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-surface-inverse-on/70 outline-none transition-colors hover:bg-white/10 hover:text-surface-inverse-on"
                    aria-label={t("nav.profile")}
                  >
                    <User className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="mt-2 w-60">
                  <ContextSwitcherMenuItems
                    onSwitch={handleSwitchContext}
                    switching={switchContext.isPending}
                  />
                  {profileLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <DropdownMenuItem
                        key={link.to}
                        onClick={() => navigate(link.to)}
                        className="cursor-pointer gap-2"
                      >
                        <Icon className="h-4 w-4" />
                        <span>{t(link.labelKey)}</span>
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer gap-2 text-error focus:text-error"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>{t("auth.logout")}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="container-page animate-fade-in flex-1 pb-6 pt-24">
        <Outlet />
      </main>

      <nav
        className="fixed bottom-0 left-0 z-50 flex w-full items-center justify-around rounded-t-2xl border-t border-white/10 bg-surface-inverse px-2 py-2.5 md:hidden"
        style={{ boxShadow: "0px -4px 20px rgba(0, 0, 0, 0.3)" }}
        aria-label={t("nav.home")}
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `flex w-14 flex-col items-center justify-center rounded-xl p-2 transition-colors duration-150 ${
                  isActive
                    ? "bg-primary/15 text-primary"
                    : "text-surface-inverse-on/45 hover:text-primary/80"
                }`
              }
            >
              <Icon className="mb-1 h-5 w-5" />
              <span className="w-full truncate text-center text-[9px] font-bold uppercase tracking-widest">
                {t(item.labelKey)}
              </span>
            </NavLink>
          );
        })}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-14 flex-col items-center justify-center p-2 text-surface-inverse-on/45 outline-none transition-colors hover:text-primary/80"
            >
              <User className="mb-1 h-5 w-5" />
              <span className="w-full truncate text-center text-[9px] font-bold uppercase tracking-widest">
                {t("nav.profile")}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={16} className="mb-2 w-60">
            <ContextSwitcherMenuItems
              onSwitch={handleSwitchContext}
              switching={switchContext.isPending}
            />
            {profileLinks.map((link) => {
              const Icon = link.icon;
              return (
                <DropdownMenuItem
                  key={link.to}
                  onClick={() => navigate(link.to)}
                  className="cursor-pointer gap-2"
                >
                  <Icon className="h-4 w-4" />
                  <span>{t(link.labelKey)}</span>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="cursor-pointer gap-2 text-error focus:text-error"
            >
              <LogOut className="h-4 w-4" />
              <span>{t("auth.logout")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
    </div>
  );
}
